/**
 * AI Navigator widget — guided onboarding + optional free chat.
 */

import {
    INTRO,
    SAFETY_DISCLAIMER,
    CRISIS_RESPONSE,
    checkCrisis,
    initNavigatorState,
    getQuestion,
    applyAnswer,
    classifyAudience,
    getRecommendationPack,
    buildPathSteps,
    buildPathMessage,
    getStepNumber,
    nextStep,
} from './ai-coach-navigator.js';

(function () {
    'use strict';

    const config = window.pe_mp_ai_coach_chat;
    if (!config || !config.apiUrl) {
        return;
    }

    const i18n = config.i18n || {};
    const STORAGE_KEY = config.storageKey || 'som-ai-coach-conversations';
    const linkCatalog = config.linkCatalog || {};
    const maxSuggestions = linkCatalog.maxSuggestions || 3;
    const mhjPathMap = linkCatalog.mhjPathMap || {};

    const SUGGESTION_ACCENTS = {
        '/screening/depression': 'blue',
        '/screening/anxiety': 'orange',
        '/screening/ptsd': 'violet',
        '/screening/bipolar': 'amber',
        '/screening/adhd': 'sky',
        '/screening/burnout': 'rose',
        '/screening/autism': 'indigo',
        '/screening/ketamine': 'violet',
        '/screening/microdosing': 'green',
        '/screening/psychedelic-therapy-starter': 'green',
        '/screening/assessment': 'violet',
        '/providers/': 'emerald',
        '/tools/': 'green',
        '/tag/': 'blue',
        '/category/paths/': 'sky',
        '/research/': 'green',
        '/podcast/': 'violet',
        '/community/': 'sky',
    };

    const root = document.getElementById('pe-ai-coach-chat');
    if (!root) return;

    const els = {
        fab: document.getElementById('pe-ai-coach-chat-fab'),
        panel: document.getElementById('pe-ai-coach-chat-panel'),
        close: document.getElementById('pe-ai-coach-chat-close'),
        newChat: document.getElementById('pe-ai-coach-chat-new'),
        sidebar: document.getElementById('pe-ai-coach-chat-sidebar'),
        sidebarToggle: document.getElementById('pe-ai-coach-chat-sidebar-toggle'),
        conversations: document.getElementById('pe-ai-coach-chat-conversations'),
        sessionLabel: document.getElementById('pe-ai-coach-chat-session-label'),
        empty: document.getElementById('pe-ai-coach-chat-empty'),
        emptyStart: document.getElementById('pe-ai-coach-chat-empty-start'),
        rateLimit: document.getElementById('pe-ai-coach-chat-rate-limit'),
        messages: document.getElementById('pe-ai-coach-chat-messages'),
        retryWrap: document.getElementById('pe-ai-coach-chat-retry-wrap'),
        retryBtn: document.getElementById('pe-ai-coach-chat-retry'),
        input: document.getElementById('pe-ai-coach-chat-input'),
        send: document.getElementById('pe-ai-coach-chat-send'),
    };

    const state = {
        open: false,
        activeConvId: null,
        messages: [],
        navigator: initNavigatorState(),
        isLoading: false,
        rateLimitUntil: null,
        retryState: null,
        sidebarOpen: false,
    };

    root.hidden = false;
    root.removeAttribute('aria-hidden');

    function generateId() {
        return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    }

    function readConversations() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    }

    function writeConversations(conversations) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations));
    }

    function getConversations() {
        return readConversations().sort((a, b) => b.updatedAt - a.updatedAt);
    }

    function getConversation(id) {
        return readConversations().find((c) => c.id === id);
    }

    function saveNavigator(convId) {
        const all = readConversations();
        const conv = all.find((c) => c.id === convId);
        if (conv) {
            conv.navigator = { ...state.navigator };
            writeConversations(all);
        }
    }

    function createConversation(title) {
        const conv = {
            id: generateId(),
            title: title || (i18n.newChat || 'New conversation'),
            messages: [],
            navigator: initNavigatorState(),
            createdAt: Date.now(),
            updatedAt: Date.now(),
        };
        const all = readConversations();
        all.push(conv);
        writeConversations(all);
        return conv;
    }

    function addMessage(conversationId, role, content, options = {}) {
        const msg = {
            id: generateId(),
            role,
            content,
            suggestions: options.suggestions || [],
            pathSteps: options.pathSteps || [],
            choices: options.choices || [],
            actions: options.actions || [],
            pendingChoices: options.pendingChoices || [],
            pendingActions: options.pendingActions || [],
            audience: options.audience || null,
            crisis: Boolean(options.crisis),
            kind: options.kind || 'normal',
            timestamp: Date.now(),
        };
        const all = readConversations();
        const conv = all.find((c) => c.id === conversationId);
        if (conv) {
            conv.messages.push(msg);
            conv.updatedAt = Date.now();
            writeConversations(all);
        }
        return msg;
    }

    function deleteConversation(id) {
        writeConversations(readConversations().filter((c) => c.id !== id));
    }

    function trackEvent(name, props) {
        if (typeof window.trackEvent === 'function') {
            window.trackEvent(name, props || {});
        }
    }

    function navAnalyticsBase() {
        return {
            page_url: window.location.href,
            referrer: document.referrer || '',
            device: window.innerWidth < 768 ? 'mobile' : 'desktop',
            user_id: config.userId || undefined,
        };
    }

    function isOnboardingActive() {
        return state.navigator && !state.navigator.complete && state.navigator.step !== 'intro';
    }

    function resolveSuggestionLink(link) {
        if (!link) return '#';
        if (/^https?:\/\//i.test(link)) return link;
        const normalized = link.startsWith('/') ? link : '/' + link;
        const base = (config.suggestionBaseUrl || config.siteUrl || '').replace(/\/$/, '');
        return base ? base + normalized : normalized;
    }

    function normalizePath(link) {
        if (!link || typeof link !== 'string') return '';
        if (/^https?:\/\//i.test(link)) {
            try {
                const url = new URL(link);
                return url.pathname.replace(/\/$/, '') || '/';
            } catch {
                return link;
            }
        }
        const path = link.startsWith('/') ? link : '/' + link;
        return path.replace(/\/$/, '') || '/';
    }

    function mapMhjPath(link) {
        const path = normalizePath(link);
        const mapped = mhjPathMap[path] || mhjPathMap[path + '/'];
        if (mapped) return normalizePath(mapped);
        const screenerMatch = path.match(/^\/([a-z0-9-]+)-screener$/i);
        if (screenerMatch) return normalizePath('/toolkit/screening/' + screenerMatch[1] + '/');
        return path;
    }

    function accentForPath(path) {
        const normalized = normalizePath(path);
        if (SUGGESTION_ACCENTS[normalized]) return SUGGESTION_ACCENTS[normalized];
        const prefixes = Object.keys(SUGGESTION_ACCENTS).sort((a, b) => b.length - a.length);
        for (const prefix of prefixes) {
            if (normalized.startsWith(prefix.replace(/\/$/, '')) || normalized.includes(prefix.replace(/\/$/, ''))) return SUGGESTION_ACCENTS[prefix];
        }
        return 'slate';
    }

    function formatSuggestionCta(suggestion) {
        const type = suggestion.type || 'hub';
        const duration = suggestion.duration || '';
        if (type === 'screening' && duration) {
            return (i18n.startTest || 'Start %s screening').replace('%s', duration);
        }
        if (type === 'tool') return i18n.openTool || 'Open tool';
        if (type === 'providers') return i18n.browseProviders || 'Browse providers';
        if (type === 'articles') return i18n.readArticles || 'Read articles';
        if (type === 'path') return i18n.startPath || 'Start here';
        return i18n.openLink || 'Open';
    }

    function escapeHtml(str) {
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    function isRateLimitMessage(text) {
        return /rate limit|too many requests/i.test(text || '');
    }

    function isRateLimited() {
        return state.rateLimitUntil !== null && state.rateLimitUntil > Date.now();
    }

    function rateLimitSeconds() {
        if (!isRateLimited()) return 0;
        return Math.max(1, Math.ceil((state.rateLimitUntil - Date.now()) / 1000));
    }

    function updateRateLimitBanner() {
        if (!els.rateLimit) return;
        if (!isRateLimited()) {
            els.rateLimit.hidden = true;
            els.rateLimit.textContent = '';
            return;
        }
        const body = (i18n.rateLimitBody || 'Please wait %ds.').replace('%d', String(rateLimitSeconds()));
        els.rateLimit.hidden = false;
        els.rateLimit.innerHTML =
            '<strong>' + escapeHtml(i18n.rateLimitTitle || 'Chat temporarily paused') + '</strong><p>' + escapeHtml(body) + '</p>';
    }

    function normalizeApiSuggestion(raw) {
        if (!raw || typeof raw !== 'object') return null;
        const link = typeof raw.link === 'string' ? mapMhjPath(raw.link) : '';
        if (typeof raw.title !== 'string' || typeof raw.description !== 'string' || !link) return null;
        const type = link.includes('/screening/') || link.includes('/tests/') ? 'screening'
            : link.includes('/providers/') ? 'providers'
            : link.includes('/tools/') ? 'tool' : 'hub';
        return {
            title: raw.title,
            description: raw.description,
            duration: typeof raw.duration === 'string' ? raw.duration : '',
            link,
            type,
            accent: accentForPath(link),
        };
    }

    function mergeSuggestions(apiSuggestions, conversationText) {
        const topics = Array.isArray(linkCatalog.topics) ? linkCatalog.topics : [];
        const haystack = (conversationText || '').toLowerCase();
        const merged = [];
        const seen = new Set();
        const push = (item) => {
            if (!item || !item.link) return;
            const path = normalizePath(item.link);
            if (seen.has(path)) return;
            seen.add(path);
            merged.push({ ...item, link: path });
        };
        (Array.isArray(apiSuggestions) ? apiSuggestions : []).forEach((raw) => {
            const n = normalizeApiSuggestion(raw);
            if (n) push(n);
        });
        if (merged.length < maxSuggestions && topics.length) {
            topics.forEach((topic) => {
                if (merged.length >= maxSuggestions) return;
                (topic.resources || []).slice(0, 1).forEach((r) => {
                    if (!r.path) return;
                    const match = (topic.keywords || []).some((k) => haystack.includes(String(k).toLowerCase()));
                    if (match) {
                        push({
                            title: r.title,
                            description: r.description,
                            duration: r.duration || '',
                            link: normalizePath(r.path),
                            type: r.type || 'hub',
                            accent: accentForPath(r.path),
                        });
                    }
                });
            });
        }
        return merged.slice(0, maxSuggestions);
    }

    async function sendChatRequest(payload) {
        const chatUrl = config.chatProxyUrl || (config.apiUrl + '/chat');
        const response = await fetch(chatUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error('Chat failed: ' + response.status);
        const data = await response.json();
        return {
            text: typeof data.text === 'string' ? data.text : '',
            crisis: Boolean(data.crisis),
            suggestions: Array.isArray(data.suggestions)
                ? data.suggestions.map(normalizeApiSuggestion).filter(Boolean)
                : [],
            rate_limited: Boolean(data.rate_limited),
        };
    }

    function renderPathSteps(steps, audience) {
        const grid = document.createElement('div');
        grid.className = 'pe-ai-coach-chat__path-steps';
        steps.forEach((step) => {
            const card = document.createElement('a');
            card.className = 'pe-ai-coach-chat__path-step pe-ai-coach-chat__suggestion pe-ai-coach-chat__suggestion--' + accentForPath(step.path);
            card.href = resolveSuggestionLink(step.path);
            card.target = '_blank';
            card.rel = 'noopener noreferrer';
            card.innerHTML =
                '<span class="pe-ai-coach-chat__path-step-label">' + escapeHtml(step.label) + '</span>' +
                '<span class="pe-ai-coach-chat__suggestion-title">' + escapeHtml(step.title) + '</span>' +
                '<span class="pe-ai-coach-chat__suggestion-desc">' + escapeHtml(step.description) + '</span>' +
                '<span class="pe-ai-coach-chat__suggestion-cta">' + escapeHtml(formatSuggestionCta(step)) + '</span>';
            card.addEventListener('click', () => {
                trackEvent('ai_recommendation_clicked', {
                    ...navAnalyticsBase(),
                    audience_type: audience,
                    recommendation_position: step.step,
                    recommendation_url: step.path,
                    recommendation_type: step.type || 'hub',
                });
            });
            grid.appendChild(card);
        });
        return grid;
    }

    function renderChoices(choices, onSelect) {
        const wrap = document.createElement('div');
        wrap.className = 'pe-ai-coach-chat__choices';
        choices.forEach((choice) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'pe-ai-coach-chat__choice';
            btn.textContent = choice.label;
            btn.addEventListener('click', () => onSelect(choice));
            wrap.appendChild(btn);
        });
        return wrap;
    }

    function renderActions(actions, onAction) {
        const wrap = document.createElement('div');
        wrap.className = 'pe-ai-coach-chat__actions';
        actions.forEach((action) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'pe-ai-coach-chat__action' + (action.primary ? ' pe-ai-coach-chat__action--primary' : '');
            btn.textContent = action.label;
            btn.addEventListener('click', () => onAction(action));
            wrap.appendChild(btn);
        });
        return wrap;
    }

    function renderMessage(msg) {
        const isUser = msg.role === 'user';
        const isSystem = msg.kind === 'system';
        const wrap = document.createElement('div');
        wrap.className = 'pe-ai-coach-chat__message-row' +
            (isSystem ? ' pe-ai-coach-chat__message-row--system' : isUser ? ' pe-ai-coach-chat__message-row--user' : ' pe-ai-coach-chat__message-row--assistant');
        wrap.dataset.messageId = msg.id;

        const bubble = document.createElement('div');
        bubble.className = 'pe-ai-coach-chat__bubble' +
            (isSystem ? ' pe-ai-coach-chat__bubble--system' : isUser ? ' pe-ai-coach-chat__bubble--user' : ' pe-ai-coach-chat__bubble--assistant');

        if (msg.crisis) {
            const crisis = document.createElement('div');
            crisis.className = 'pe-ai-coach-chat__crisis';
            crisis.innerHTML =
                '<strong>' + escapeHtml(i18n.crisisTitle || 'Need immediate support?') + '</strong>' +
                '<p>' + escapeHtml(i18n.crisisBody || '') + '</p>';
            bubble.appendChild(crisis);
        }

        const text = document.createElement('p');
        text.className = 'pe-ai-coach-chat__bubble-text';
        text.textContent = msg.content;
        bubble.appendChild(text);

        if (msg.pathSteps && msg.pathSteps.length > 0) {
            bubble.appendChild(renderPathSteps(msg.pathSteps, msg.audience || state.navigator.audience));
        }

        if (!msg.crisis && msg.suggestions && msg.suggestions.length > 0) {
            const grid = document.createElement('div');
            grid.className = 'pe-ai-coach-chat__suggestions';
            msg.suggestions.forEach((s) => {
                const card = document.createElement('a');
                card.className = 'pe-ai-coach-chat__suggestion pe-ai-coach-chat__suggestion--' + (s.accent || 'slate');
                card.href = resolveSuggestionLink(s.link);
                card.target = '_blank';
                card.rel = 'noopener noreferrer';
                card.innerHTML =
                    '<span class="pe-ai-coach-chat__suggestion-title">' + escapeHtml(s.title) + '</span>' +
                    '<span class="pe-ai-coach-chat__suggestion-desc">' + escapeHtml(s.description) + '</span>' +
                    '<span class="pe-ai-coach-chat__suggestion-cta">' + escapeHtml(formatSuggestionCta(s)) + '</span>';
                grid.appendChild(card);
            });
            bubble.appendChild(grid);
        }

        if (msg.choices && msg.choices.length > 0 && msg._choicesHandler) {
            bubble.appendChild(renderChoices(msg.choices, msg._choicesHandler));
        }

        if (msg.actions && msg.actions.length > 0 && msg._actionsHandler) {
            bubble.appendChild(renderActions(msg.actions, msg._actionsHandler));
        }

        wrap.appendChild(bubble);
        return wrap;
    }

    function attachInteractiveHandlers() {
        let lastChoiceIdx = -1;
        let lastActionIdx = -1;
        state.messages.forEach((msg, idx) => {
            if (msg.pendingChoices) lastChoiceIdx = idx;
            if (msg.pendingActions) lastActionIdx = idx;
        });
        state.messages.forEach((msg, idx) => {
            if (msg.pendingChoices && idx === lastChoiceIdx && !state.navigator.complete) {
                msg._choicesHandler = (choice) => handleChoiceAnswer(choice);
                msg.choices = msg.pendingChoices;
            } else {
                msg.choices = [];
            }
            if (msg.pendingActions && idx === lastActionIdx) {
                msg._actionsHandler = (action) => handlePathAction(action);
                msg.actions = msg.pendingActions;
            } else {
                msg.actions = [];
            }
        });
    }

    function renderMessages() {
        if (!els.messages || !els.empty) return;

        attachInteractiveHandlers();
        els.messages.innerHTML = '';
        const showEmpty = !state.activeConvId && state.messages.length === 0;
        els.empty.hidden = !showEmpty;
        els.messages.hidden = state.messages.length === 0 && !showEmpty ? true : state.messages.length === 0;

        state.messages.forEach((msg) => {
            els.messages.appendChild(renderMessage(msg));
        });

        if (state.isLoading) {
            const typing = document.createElement('div');
            typing.className = 'pe-ai-coach-chat__message-row pe-ai-coach-chat__message-row--assistant';
            typing.innerHTML =
                '<div class="pe-ai-coach-chat__bubble pe-ai-coach-chat__bubble--assistant pe-ai-coach-chat__bubble--typing">' +
                '<span class="pe-ai-coach-chat__typing-spinner" aria-hidden="true"></span>' +
                escapeHtml(i18n.thinking || 'Thinking…') + '</div>';
            els.messages.appendChild(typing);
        }

        updateRateLimitBanner();
        updateComposerState();
        scrollToBottom();
    }

    function scrollToBottom() {
        const wrap = els.messages && els.messages.parentElement;
        if (wrap) wrap.scrollTop = wrap.scrollHeight;
    }

    function updateComposerState() {
        if (!els.input || !els.send) return;
        const hasText = els.input.value.trim().length > 0;
        const disabled = !hasText || state.isLoading || isRateLimited();
        els.send.disabled = disabled;
        els.input.disabled = isRateLimited();
        if (els.input) {
            els.input.placeholder = state.navigator.complete
                ? (i18n.placeholder || "What's on your mind?")
                : (i18n.placeholderOnboarding || 'Or type a question anytime…');
        }
    }

    function pushAssistantQuestion(step) {
        const question = getQuestion(step, state.navigator);
        if (!question) return;

        const msg = addMessage(state.activeConvId, 'assistant', question.question, {
            pendingChoices: question.options.map((o) => ({
                id: o.id,
                label: o.label,
                option: o,
                questionId: question.id,
            })),
        });
        msg.pendingChoices = question.options.map((o) => ({
            id: o.id,
            label: o.label,
            option: o,
            questionId: question.id,
        }));
        state.messages.push(msg);
        state.navigator.step = step;
        saveNavigator(state.activeConvId);
        renderMessages();
    }

    function handleCrisis(convId, source) {
        state.navigator.complete = true;
        state.navigator.step = 'crisis';
        saveNavigator(convId);
        const msg = addMessage(convId, 'assistant', CRISIS_RESPONSE, { crisis: true, kind: 'system' });
        state.messages.push(msg);
        renderMessages();
        trackEvent('ai_navigator_crisis', { ...navAnalyticsBase(), source: source || 'text' });
    }

    function startOnboarding(entryMode) {
        let convId = state.activeConvId;
        if (!convId) {
            const conv = createConversation(i18n.navigatorTitle || 'AI Navigator');
            convId = conv.id;
            state.activeConvId = convId;
            state.navigator = conv.navigator;
        }
        state.navigator = initNavigatorState();
        state.navigator.step = 'q1';
        state.messages = [];
        saveNavigator(convId);

        trackEvent('ai_onboarding_started', {
            ...navAnalyticsBase(),
            source_page: window.location.pathname,
            entry_mode: entryMode || 'button',
        });

        updateSessionLabel();
        pushAssistantQuestion('q1');
        renderConversationList();
    }

    function completeOnboarding() {
        const audience = classifyAudience(state.navigator);
        const pack = getRecommendationPack(state.navigator);
        const pathSteps = buildPathSteps(pack);
        state.navigator.complete = true;
        state.navigator.step = 'complete';
        state.navigator.primaryPathUrl = pathSteps[0].path;
        saveNavigator(state.activeConvId);

        const topScore = Math.max(...Object.values(state.navigator.scores || {}));
        trackEvent('ai_audience_classified', {
            ...navAnalyticsBase(),
            audience_type: audience,
            confidence_score: topScore,
        });
        trackEvent('ai_path_generated', {
            ...navAnalyticsBase(),
            audience_type: audience,
            primary_recommendation_url: pathSteps[0].path,
            supporting_recommendation_1_url: pathSteps[1].path,
            supporting_recommendation_2_url: pathSteps[2].path,
        });

        const content = buildPathMessage() + '\n\n' + SAFETY_DISCLAIMER;
        const msg = addMessage(state.activeConvId, 'assistant', content, {
            pathSteps,
            audience,
            pendingActions: [
                { id: 'follow_up', label: i18n.askFollowUp || 'Ask a follow-up question' },
                { id: 'restart', label: i18n.restartPath || 'Restart recommendation' },
            ],
        });
        msg.pathSteps = pathSteps;
        msg.audience = audience;
        msg.pendingActions = [
            { id: 'follow_up', label: i18n.askFollowUp || 'Ask a follow-up question' },
            { id: 'restart', label: i18n.restartPath || 'Restart recommendation' },
        ];
        state.messages.push(msg);
        renderMessages();
        renderConversationList();
    }

    function handleChoiceAnswer(choice) {
        if (!state.activeConvId || !choice || !choice.option) return;

        const step = state.navigator.step;
        const question = getQuestion(step, state.navigator);
        if (!question) return;

        const userMsg = addMessage(state.activeConvId, 'user', choice.label);
        state.messages.push(userMsg);

        trackEvent('ai_question_answered', {
            ...navAnalyticsBase(),
            question_id: question.id,
            answer: choice.id,
            step_number: getStepNumber(step),
        });

        applyAnswer(state.navigator, question.id, choice.option);
        saveNavigator(state.activeConvId);

        const next = nextStep(step);
        if (next === 'complete') {
            completeOnboarding();
            return;
        }
        pushAssistantQuestion(next);
        renderConversationList();
    }

    function handlePathAction(action) {
        if (!action) return;
        if (action.id === 'restart') {
            trackEvent('ai_path_restart', { ...navAnalyticsBase(), audience_type: state.navigator.audience });
            startOnboarding('restart');
            return;
        }
        if (action.id === 'follow_up') {
            trackEvent('ai_free_chat_started_after_onboarding', {
                ...navAnalyticsBase(),
                audience_type: state.navigator.audience,
                previous_primary_recommendation_url: state.navigator.primaryPathUrl,
            });
            if (els.input) els.input.focus();
        }
    }

    async function requestAssistantMessage(convId, apiMessages) {
        state.isLoading = true;
        if (els.retryWrap) els.retryWrap.hidden = true;
        renderMessages();

        try {
            const response = await sendChatRequest({
                conversation_id: convId,
                user_id: config.userId || undefined,
                messages: apiMessages,
            });

            if (response.crisis || checkCrisis(response.text)) {
                handleCrisis(convId, 'api');
                return;
            }

            const limited = response.rate_limited || isRateLimitMessage(response.text);
            state.rateLimitUntil = limited ? Date.now() + 60000 : null;

            const contextText = apiMessages.map((m) => m.content).join(' ');
            const suggestions = mergeSuggestions(response.suggestions, contextText);

            const assistantMsg = addMessage(convId, 'assistant', response.text, {
                suggestions,
                kind: limited ? 'system' : 'normal',
            });
            state.messages.push(assistantMsg);
            state.retryState = null;
        } catch {
            trackEvent('chat_error', { error_code: 'request_failed' });
            const fallback = i18n.errorFallback || 'Connection error.';
            state.messages.push(addMessage(convId, 'assistant', fallback, { kind: 'system' }));
            state.retryState = { convId, apiMessages };
            if (els.retryWrap) els.retryWrap.hidden = false;
        } finally {
            state.isLoading = false;
            renderMessages();
            renderConversationList();
        }
    }

    async function sendFreeChatMessage(text, convId) {
        if (!state.navigator.complete) {
            trackEvent('ai_free_chat_started_after_onboarding', {
                ...navAnalyticsBase(),
                audience_type: state.navigator.audience || 'unknown',
                entry_mode: 'free_text_interrupt',
            });
            state.navigator.complete = true;
            state.navigator.step = 'complete';
            saveNavigator(convId);
        }

        const userMsg = addMessage(convId, 'user', text);
        state.messages.push(userMsg);
        renderMessages();
        renderConversationList();
        updateSessionLabel();

        trackEvent('chat_message_sent', {
            message_length: text.length,
            has_context: state.messages.length > 1,
            mode: 'free_chat',
        });

        const conversation = getConversation(convId);
        const apiMessages = (conversation?.messages || [])
            .filter((m) => m.role === 'user' || m.role === 'assistant')
            .filter((m) => !m.crisis && m.kind !== 'system')
            .map((m) => ({ role: m.role, content: m.content }));

        await requestAssistantMessage(convId, apiMessages);
    }

    function handleSend() {
        if (!els.input || state.isLoading || isRateLimited()) return;
        const text = els.input.value.trim();
        if (!text) return;
        els.input.value = '';
        updateComposerState();

        if (checkCrisis(text)) {
            let convId = state.activeConvId;
            if (!convId) {
                const conv = createConversation(text.slice(0, 60));
                convId = conv.id;
                state.activeConvId = convId;
            }
            state.messages.push(addMessage(convId, 'user', text));
            handleCrisis(convId, 'user_input');
            return;
        }

        let convId = state.activeConvId;
        if (!convId) {
            const conv = createConversation(text.slice(0, 60));
            convId = conv.id;
            state.activeConvId = convId;
            state.navigator = conv.navigator;
            trackEvent('ai_onboarding_started', {
                ...navAnalyticsBase(),
                source_page: window.location.pathname,
                entry_mode: 'free_text',
            });
        }

        if (!state.navigator.complete && state.navigator.step === 'intro') {
            state.navigator.complete = true;
            saveNavigator(convId);
        }

        sendFreeChatMessage(text, convId);
    }

    function renderConversationList() {
        if (!els.conversations) return;
        const convs = getConversations();
        els.conversations.innerHTML = '';
        if (convs.length === 0) {
            const empty = document.createElement('p');
            empty.className = 'pe-ai-coach-chat__sidebar-empty';
            empty.textContent = i18n.noSessions || 'No sessions yet';
            els.conversations.appendChild(empty);
            return;
        }
        convs.forEach((conv) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'pe-ai-coach-chat__conv-item' +
                (conv.id === state.activeConvId ? ' pe-ai-coach-chat__conv-item--active' : '');
            btn.innerHTML =
                '<span class="pe-ai-coach-chat__conv-title">' + escapeHtml(conv.title) + '</span>' +
                '<span class="pe-ai-coach-chat__conv-date">' + escapeHtml(new Date(conv.updatedAt).toLocaleDateString()) + '</span>';
            const del = document.createElement('button');
            del.type = 'button';
            del.className = 'pe-ai-coach-chat__conv-delete';
            del.setAttribute('aria-label', i18n.deleteConversation || 'Delete conversation');
            del.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" stroke-width="1.75"/></svg>';
            del.addEventListener('click', (e) => {
                e.stopPropagation();
                deleteConversation(conv.id);
                if (state.activeConvId === conv.id) resetActiveConversation();
                renderConversationList();
            });
            btn.addEventListener('click', () => selectConversation(conv.id));
            const row = document.createElement('div');
            row.className = 'pe-ai-coach-chat__conv-row';
            row.appendChild(btn);
            row.appendChild(del);
            els.conversations.appendChild(row);
        });
    }

    function updateSessionLabel() {
        if (!els.sessionLabel) return;
        if (!state.activeConvId) {
            els.sessionLabel.textContent = i18n.navigatorTitle || 'AI Navigator';
            return;
        }
        const conv = getConversation(state.activeConvId);
        els.sessionLabel.textContent = conv ? conv.title : (i18n.navigatorTitle || 'AI Navigator');
    }

    function resetActiveConversation() {
        state.activeConvId = null;
        state.messages = [];
        state.navigator = initNavigatorState();
        state.retryState = null;
        if (els.retryWrap) els.retryWrap.hidden = true;
        updateSessionLabel();
        renderMessages();
        renderConversationList();
    }

    function selectConversation(id) {
        const conv = getConversation(id);
        if (!conv) return;
        state.activeConvId = conv.id;
        state.messages = conv.messages.slice();
        state.navigator = conv.navigator ? { ...conv.navigator } : initNavigatorState();
        state.retryState = null;
        if (els.retryWrap) els.retryWrap.hidden = true;
        updateSessionLabel();
        renderMessages();
        renderConversationList();
        closeSidebarMobile();
    }

    function openPanel() {
        state.open = true;
        root.classList.add('pe-ai-coach-chat--open');
        if (els.panel) {
            els.panel.hidden = false;
            els.panel.removeAttribute('hidden');
        }
        if (els.fab) els.fab.setAttribute('aria-expanded', 'true');
        document.body.classList.add('pe-ai-coach-chat-body-lock');
        trackEvent('ai_navigator_opened', navAnalyticsBase());
        renderConversationList();
        renderMessages();
        if (els.input) setTimeout(() => els.input.focus(), 100);
    }

    function closePanel() {
        state.open = false;
        root.classList.remove('pe-ai-coach-chat--open');
        if (els.panel) els.panel.hidden = true;
        if (els.fab) els.fab.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('pe-ai-coach-chat-body-lock');
        closeSidebarMobile();
        trackEvent('chat_closed', { source: 'close_button' });
    }

    function toggleSidebarMobile() {
        if (!els.sidebar) return;
        state.sidebarOpen = !state.sidebarOpen;
        els.sidebar.classList.toggle('pe-ai-coach-chat__sidebar--open', state.sidebarOpen);
    }

    function closeSidebarMobile() {
        state.sidebarOpen = false;
        if (els.sidebar) els.sidebar.classList.remove('pe-ai-coach-chat__sidebar--open');
    }

    function setupLoggedInUi() {
        const loggedIn = Boolean(config.isLoggedIn);
        if (els.sidebar) {
            els.sidebar.hidden = false;
            els.sidebar.classList.remove('pe-ai-coach-chat__sidebar--open');
        }
        if (els.sidebarToggle) els.sidebarToggle.hidden = !loggedIn;
    }

    if (els.fab) els.fab.addEventListener('click', openPanel);
    if (els.close) els.close.addEventListener('click', closePanel);
    if (els.newChat) els.newChat.addEventListener('click', resetActiveConversation);
    if (els.sidebarToggle) els.sidebarToggle.addEventListener('click', toggleSidebarMobile);
    if (els.send) els.send.addEventListener('click', handleSend);
    if (els.emptyStart) els.emptyStart.addEventListener('click', () => startOnboarding('button'));
    if (els.retryBtn) {
        els.retryBtn.addEventListener('click', () => {
            if (!state.retryState) return;
            requestAssistantMessage(state.retryState.convId, state.retryState.apiMessages);
        });
    }
    if (els.input) {
        els.input.addEventListener('input', updateComposerState);
        els.input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
            }
        });
    }
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && state.open) closePanel();
    });
    setInterval(() => {
        if (isRateLimited()) {
            updateRateLimitBanner();
            updateComposerState();
        } else if (state.rateLimitUntil !== null) {
            state.rateLimitUntil = null;
            updateRateLimitBanner();
            updateComposerState();
        }
    }, 1000);

    setupLoggedInUi();
    updateSessionLabel();
    renderMessages();
})();
